import { Helmet } from 'react-helmet-async';
import { filter } from 'lodash';
import { sentenceCase } from 'change-case';
import * as FileSaver from 'file-saver';
import * as React from 'react';
import XLSX from 'sheetjs-style';
import PropTypes from 'prop-types';
import { useTheme } from '@mui/material/styles';
import moment from 'moment';

import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, rectSortingStrategy } from '@dnd-kit/sortable';

// @mui
import {
  Card,
  Table,
  Stack,
  Paper,
  Avatar,
  Button,
  Popover,
  Checkbox,
  TableRow,
  MenuItem,
  TableBody,
  TableCell,
  Container,
  Typography,
  Tooltip,
  IconButton,
  TableContainer,
  TablePagination,
  Box,
  Grid,
} from '@mui/material';

import Link from '@mui/material/Link';
import TextField from '@mui/material/TextField';
import PhoneInput from 'react-phone-input-2';

import FirstPageIcon from '@mui/icons-material/FirstPage';
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import LastPageIcon from '@mui/icons-material/LastPage';
import dayjs from 'dayjs';

import { DemoContainer } from '@mui/x-date-pickers/internals/demo';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

import { useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import CircularProgress from '@mui/material/CircularProgress';

import {
  collection,
  getDocs,
  getFirestore,
  addDoc,
  updateDoc,
  doc,
  serverTimestamp,
  onSnapshot,
  query,
  orderBy,
  limitToLast,
  Timestamp,
  writeBatch,
} from 'firebase/firestore';
import { getDatabase, ref as dbref, runTransaction, set, child, get, update, remove } from 'firebase/database';
import { initializeApp } from 'firebase/app';
import { getStorage, ref, getDownloadURL } from 'firebase/storage';

import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';

import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import SortableItem from '../../components/dnd/SortableItem';

// components
import Label from '../../components/label';
import Iconify from '../../components/iconify';
import Scrollbar from '../../components/scrollbar';
// sections
import { UserListHead, UserListToolbar } from '../../sections/@dashboard/microsite';

import { UserAuth } from '../../context/AuthContext';

const firebaseConfig = {
  apiKey: 'AIzaSyDLVrlBPuai36n-i4F5HQVTjSMkVeGVBak',
  authDomain: 'onetweb-e3d8e.firebaseapp.com',
  databaseURL: 'https://onetweb-e3d8e-default-rtdb.asia-southeast1.firebasedatabase.app',
  projectId: 'onetweb-e3d8e',
  storageBucket: 'onetweb-e3d8e.firebasestorage.app',
  messagingSenderId: '60910494595',
  appId: '1:60910494595:web:2dc6bf320d67371af04cb6',
  measurementId: 'G-0ZTHXZBNP9',
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);

// Initialize Cloud Firestore and get a reference to the service
const db = getFirestore(app);

const rtdb = getDatabase(app);

// ----------------------------------------------------------------------

function TablePaginationActions(props) {
  const theme = useTheme();
  const { count, page, rowsPerPage, onPageChange } = props;

  const handleFirstPageButtonClick = (event) => {
    onPageChange(event, 0);
  };

  const handleBackButtonClick = (event) => {
    onPageChange(event, page - 1);
  };

  const handleNextButtonClick = (event) => {
    onPageChange(event, page + 1);
  };

  const handleLastPageButtonClick = (event) => {
    onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
  };

  return (
    <Box sx={{ flexShrink: 0, ml: 2.5 }}>
      <IconButton onClick={handleFirstPageButtonClick} disabled={page === 0} aria-label="first page">
        {theme.direction === 'rtl' ? <LastPageIcon /> : <FirstPageIcon />}
      </IconButton>
      <IconButton onClick={handleBackButtonClick} disabled={page === 0} aria-label="previous page">
        {theme.direction === 'rtl' ? <KeyboardArrowRight /> : <KeyboardArrowLeft />}
      </IconButton>
      <IconButton
        onClick={handleNextButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="next page"
      >
        {theme.direction === 'rtl' ? <KeyboardArrowLeft /> : <KeyboardArrowRight />}
      </IconButton>
      <IconButton
        onClick={handleLastPageButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="last page"
      >
        {theme.direction === 'rtl' ? <FirstPageIcon /> : <LastPageIcon />}
      </IconButton>
    </Box>
  );
}

TablePaginationActions.propTypes = {
  count: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  page: PropTypes.number.isRequired,
  rowsPerPage: PropTypes.number.isRequired,
};

function descendingComparator(a, b, orderBy2) {
  if (b[orderBy2] < a[orderBy2]) {
    return -1;
  }
  if (b[orderBy2] > a[orderBy2]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy2) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy2)
    : (a, b) => -descendingComparator(a, b, orderBy2);
}

function applySortFilter(array, comparator, query, select) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  if (query) {
    if (select === 'title') {
      return filter(array, (_user) => _user.title.toLowerCase().indexOf(query.toLowerCase()) !== -1);
    }

    if (select === 'id') {
      return filter(array, (_user) => _user.id.toLowerCase().indexOf(query.toLowerCase()) !== -1);
    }

    if (select === 'prefix') {
      return filter(array, (_user) => _user.prefix.toLowerCase().indexOf(query.toLowerCase()) !== -1);
    }
  }
  return stabilizedThis.map((el) => el[0]);
}

const fileType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
const fileExtension = '.xlsx';

export default function ProductPage() {
  const { user } = UserAuth();

  const [open, setOpen] = useState(null);

  const [page, setPage] = useState(0);

  const [order, setOrder] = useState('desc');

  const [selected, setSelected] = useState([]);

  const [orderBy2, setOrderBy2] = useState('created');

  const [filterName, setFilterName] = useState('');

  const [rowsPerPage, setRowsPerPage] = useState(5);

  const [row, setRow] = useState([]);

  const [fileUrl, setFileUrl] = useState(null);

  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);

  const [statusdevice, setStatus] = useState('');
  const [emailaddress, setEmail] = useState('');
  const [cityname, setCity] = useState('');
  const [activationDate, setActivationDate] = useState('');
  const [name, setName] = useState('');
  const [mobileNum, setMobileNum] = React.useState('');
  const [userRole, setUserRole] = useState('super_admin');

  const [activeId, setActiveId] = useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  const handleDragEnd = (event) => {
    setActiveId(null);
    const { active, over } = event;

    if (active.id !== over.id) {
      setRow((items) => {
        const oldIndex = items.map((val) => val.id).indexOf(active.id);
        const newIndex = items.map((val) => val.id).indexOf(over.id);

        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  const handleChangeStatus = (event) => {
    setStatus(event.target.value);
  };

  const [select, setSelect] = useState('id');

  const handleChangeSelect = (event) => {
    setSelect(event.target.value);
  };

  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogOpen2, setDialogOpen2] = useState(false);
  const [dialogOpen3, setDialogOpen3] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [openDetails, setOpenDetails] = useState(false);
  const [dialogLoading, setDialogLoading] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);

  const handleCloseDetails = () => {
    setOpenDetails(false);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  const handleDialogClose2 = () => {
    setDialogOpen2(false);
  };

  const handleDialogClose3 = () => {
    setDialogOpen3(false);
  };

  const handleDialogSubmit = () => {
    setOpenDelete(false);
  };

  const handleOnDelete = (id) => {
    setSelectedItem(id);
    setOpenDelete(true);
  };

  const handleCloseDelete = () => {
    setOpenDelete(false);
  };

  const handleSubmitDelete = async () => {
    const currentIndex = row.map((val) => val.id).indexOf(selectedItem);

    if (currentIndex > -1) {
      row.splice(currentIndex, 1);
    }

    set(dbref(rtdb, `partnerlogo`), row).then(() => {
      setOpenDelete(false);
    });
  };

  useEffect(() => {
    let _user = '';
    if (user && user.reloadUserInfo && user.reloadUserInfo.customAttributes) {
      _user = JSON.parse(user.reloadUserInfo.customAttributes);
    }
    setUserRole(_user.role);

    const arr = [];
    get(child(dbref(rtdb), `partnerlogo`))
      .then((snapshot) => {
        if (snapshot.exists()) {
          snapshot.forEach((doc) => {
            arr.push({
              ...doc.val(),
            });
          });

          setRow(arr);
          setLoading(false);
        } else {
          setRow([]);
          setLoading(false);
          console.log('No data available');
        }
      })
      .catch((error) => {
        console.error(error);
      });
  }, []);

  const handleChangeMobileNum = (event) => {
    setMobileNum(event.trim());
  };

  const handleChangeEmail = (event) => {
    setEmail(event.target.value.trim());
  };

  const handleChangeCity = (event) => {
    setCity(event.target.value.trim());
  };

  const handleChangeActivationDate = (event) => {
    setActivationDate(event.target.value.trim());
  };

  const handleChangeName = (event) => {
    setName(event.target.value.trim());
  };

  const downloadExcel = async () => {
    setDialogOpen2(true);
  };

  const downloadExcelCreatedDate = async () => {
    setDialogOpen3(true);
  };

  const handleSubmitChanges = () => {
    set(dbref(rtdb, `partnerlogo`), row);
  };

  const handleOnEdit = () => {
    setEmail(selectedItem.email);
    setActivationDate(selectedItem.purchase_date);
    setCity(selectedItem.city);
    setName(selectedItem.full_name);
    setMobileNum(selectedItem.mobile);
    setDialogOpen(true);
    setOpen(false);
  };

  const handleOpenMenu = (event, row) => {
    setSelectedItem(row);
    setOpen(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setOpen(null);
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy2 === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy2(property);
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = row.map((n) => n.id);
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  };

  const handleClick = (event, name) => {
    const selectedIndex = selected.indexOf(name);
    let newSelected = [];
    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, name);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(selected.slice(0, selectedIndex), selected.slice(selectedIndex + 1));
    }
    setSelected(newSelected);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setPage(0);
    setRowsPerPage(parseInt(event.target.value, 10));
  };

  const handleFilterByName = (event) => {
    setPage(0);
    setFilterName(event.target.value);
  };

  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - row.length) : 0;

  const filteredUsers = applySortFilter(row, getComparator(order, orderBy2), filterName, select);

  const isNotFound = !filteredUsers.length && !!filterName;

  if (loading) {
    return (
      <Grid style={{ width: '100%', display: 'flex', alignItems: 'center', flexDirection: 'column' }}>
        <Grid
          style={{
            width: '100%',
            marginTop: 60,
            maxWidth: 1200,
            display: 'flex',
            padding: 25,
            height: '80vh',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Box sx={{ display: 'flex' }}>
            <CircularProgress color="inherit" />
          </Box>
        </Grid>
      </Grid>
    );
  }

  const renderStatus = (status) => {
    let _status = '';
    if (status === 'pending_verification') {
      _status = 'Pending Verification';
    } else if (status === 'verified') {
      _status = 'Verified';
    }

    return _status;
  };

  const renderFilePath = (path) => {
    const printUrl = async (_path) => {
      const storage = getStorage(app);
      const _url = await getDownloadURL(ref(storage, _path))
        .then((url) => url)
        .catch((error) => {
          // Handle any errors
        });
      setFileUrl(_url);
    };

    printUrl(path);

    return fileUrl;
  };

  return (
    <>
      <Helmet>
        <title> Partners | OneT </title>
      </Helmet>

      <Dialog
        open={dialogOpen}
        onClose={handleDialogClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">Edit</DialogTitle>
        <DialogContent>
          <FormControl style={{ marginTop: 20 }} fullWidth>
            <InputLabel id="demo-simple-select-label">Status</InputLabel>
            <Select labelId="Status" id="Status" value={statusdevice} label="Status" onChange={handleChangeStatus}>
              <MenuItem value="pending_verification">Pending Verification</MenuItem>
              <MenuItem value="verified">Verified</MenuItem>
            </Select>
          </FormControl>

          <TextField
            style={{ margin: 0, marginTop: 15, width: '100%' }}
            value={emailaddress}
            onChange={handleChangeEmail}
            autoComplete="off"
            id="outlined-required"
            label="Email Address"
          />

          <TextField
            style={{ margin: 0, marginTop: 15, width: '100%' }}
            value={cityname}
            onChange={handleChangeCity}
            autoComplete="off"
            id="outlined-required"
            label="City"
          />

          <TextField
            style={{ margin: 0, marginTop: 15, width: '100%' }}
            value={activationDate}
            onChange={handleChangeActivationDate}
            autoComplete="off"
            id="outlined-required"
            label="Activation Date"
          />

          <TextField
            style={{ margin: 0, marginTop: 15, width: '100%' }}
            value={name}
            onChange={handleChangeName}
            autoComplete="off"
            id="outlined-required"
            label="Name"
          />

          <PhoneInput
            country="kh"
            inputStyle={{ width: '100%' }}
            containerStyle={{ width: '100%' }}
            style={{ margin: 0, marginTop: 15, width: '100%' }}
            value={mobileNum}
            onChange={handleChangeMobileNum}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose}>No</Button>
          <Button onClick={handleDialogSubmit} autoFocus>
            Yes
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openDelete}
        onClose={handleCloseDelete}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">Delete Logo</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to delete this logo?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDelete}>No</Button>
          <Button onClick={handleSubmitDelete} autoFocus>
            Yes
          </Button>
        </DialogActions>
      </Dialog>

      <Container>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
          <Typography variant="h4" gutterBottom>
            Partners
          </Typography>
          <Button variant="contained" href="/dashboard/new_partner" startIcon={<Iconify icon="eva:plus-fill" />}>
            New Partner
          </Button>
        </Stack>

        <Card>
          <Scrollbar>
            {row.length > 0 ? (
              <Grid
                style={{
                  padding: 20,
                  paddingBottom: 30,
                  display: 'flex',
                  flexDirection: 'column',
                  alignContent: 'flex-end',
                }}
              >
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                  onDragStart={handleDragStart}
                >
                  <Grid container style={{ maxWidth: 800 }}>
                    <SortableContext items={row} strategy={rectSortingStrategy}>
                      {row.map((val) => (
                        <SortableItem key={val.id} id={val.id} value={val} handleDelete={handleOnDelete} />
                      ))}
                      <DragOverlay>
                        {activeId ? (
                          <Grid
                            style={{
                              borderRadius: '5px',
                              width: 180,
                              height: 94,
                              backgroundColor: 'gray',
                            }}
                          >
                            <img src={row[row.map((val) => val.id).indexOf(activeId)].img_fullPath} alt={activeId} />
                          </Grid>
                        ) : null}
                      </DragOverlay>
                    </SortableContext>
                  </Grid>
                </DndContext>
                <Button onClick={handleSubmitChanges} variant="contained">
                  Save Changes
                </Button>
              </Grid>
            ) : (
              <Grid>
                <Typography style={{ fontWeight: 'bold', fontSize: 14, color: 'black', padding: 10 }}>
                  Start Upload Your Partner logos
                </Typography>
              </Grid>
            )}
          </Scrollbar>
        </Card>
      </Container>

      <Popover
        open={Boolean(open)}
        anchorEl={open}
        onClose={handleCloseMenu}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            p: 1,
            width: 140,
            '& .MuiMenuItem-root': {
              px: 1,
              typography: 'body2',
              borderRadius: 0.75,
            },
          },
        }}
      >
        <MenuItem onClick={handleOnDelete} sx={{ color: 'error.main' }}>
          <Iconify icon="eva:trash-2-outline" sx={{ mr: 2 }} />
          Delete
        </MenuItem>
      </Popover>
    </>
  );
}
